import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';

/**
 * M18 L18.01 — In-app notification inbox for the current user.
 *
 * Reads `GET /notifications` (history by recipient) + `GET /notifications/unread-count`.
 * Supports marking a single notification read (`PATCH /notifications/:id/read`) and
 * marking all read (`PATCH /notifications/read-all`).
 */
interface NotificationRow {
  readonly historyId: string;
  readonly channel: string;
  readonly subject: string | null;
  readonly body: string | null;
  readonly status: string;
  readonly readAt: string | null;
  readonly createdAt?: string;
}

interface ListResponse {
  readonly success: boolean;
  readonly data: NotificationRow[];
  readonly total: number;
}

export function NotificationInboxPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<NotificationRow[] | null>(null);
  const [unread, setUnread] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setRows(null);
    setError(null);
    try {
      const [list, count] = await Promise.all([
        apiRequest<ListResponse>('/notifications?page=1&limit=50', { token }),
        apiRequest<{ data: { unreadCount: number } }>('/notifications/unread-count', { token }),
      ]);
      setRows(list.data ?? []);
      setUnread(count.data?.unreadCount ?? 0);
    } catch (e) {
      setError((e as Error).message);
      setRows([]);
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  const markRead = useCallback(async (id: string) => {
    try {
      await apiRequest(`/notifications/${encodeURIComponent(id)}/read`, { method: 'PATCH', token });
      await load();
    } catch (e) { setError((e as Error).message); }
  }, [token, load]);

  const markAllRead = useCallback(async () => {
    try {
      await apiRequest('/notifications/read-all', { method: 'PATCH', token });
      await load();
    } catch (e) { setError((e as Error).message); }
  }, [token, load]);

  return (
    <main aria-label="notification-inbox">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
        <h1 style={{ fontSize: 20 }}>{t('notif.inbox.title')}</h1>
        <span data-testid="unread-count" style={{ fontSize: 12, padding: '2px 8px', borderRadius: 10, background: 'var(--color-info-bg)', color: 'var(--color-text-link)' }}>
          {t('notif.inbox.unread', { count: unread })}
        </span>
        <Button type="button" data-testid="mark-all-read" onClick={markAllRead} variant="secondary"
                style={{ marginLeft: 'auto' }}>
          {t('notif.inbox.markAllRead')}
        </Button>
      </div>

      {error && <p role="alert" style={{ color: 'var(--color-danger)' }}>{error}</p>}
      {!rows ? (
        <p>{t('notif.common.loading')}</p>
      ) : rows.length === 0 ? (
        <p data-testid="inbox-empty" style={{ color: 'var(--color-text-muted)' }}>{t('notif.inbox.empty')}</p>
      ) : (
        <ul aria-label="notifications-list" style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {rows.map((n) => (
            <li key={n.historyId} data-testid={`notification-${n.historyId}`}
                style={{ border: '1px solid var(--color-border-default)', borderRadius: 6, padding: 12,
                         background: n.readAt ? 'var(--color-bg-surface)' : 'var(--color-bg-muted)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong>{n.subject ?? t('notif.inbox.noSubject')}</strong>
                <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{n.channel} · {n.status}</span>
              </div>
              {n.body && <p style={{ margin: '6px 0 0', fontSize: 13 }}>{n.body}</p>}
              {!n.readAt && (
                <Button type="button" data-testid={`mark-read-${n.historyId}`} onClick={() => markRead(n.historyId)} variant="primary"
                        style={{ marginTop: 8 }}>
                  {t('notif.inbox.markRead')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
